import React, { useEffect, useState } from 'react';
import { CheckCircle, Clock, X, ChevronLeft, Home, CreditCard, Sparkles, Calendar, MousePointer, Check, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
interface PianificaPuliziaWizardProps {
  isOpen: boolean;
  onClose: () => void;
}
interface AiSuggestion {
  id: string;
  date: string;
  dayName: string;
  dayNumber: string;
  month: string;
  time: string;
  checkOut: string;
  checkIn: string;
  type: 'standard' | 'express';
  confidence: number;
  reason: string;
  selected: boolean;
}
export function PianificaPuliziaWizard({
  isOpen,
  onClose
}: PianificaPuliziaWizardProps) {
  const [wizardMode, setWizardMode] = useState<'choice' | 'manual' | 'ai'>('choice');
  const [wizardStep, setWizardStep] = useState(0); // 0 = choice, 1-4 = manual steps, or AI steps
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  // AI mode states
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<AiSuggestion[]>([]);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  // Mock AI suggestions
  const mockAiSuggestions: AiSuggestion[] = [{
    id: 'ai-1',
    date: '2024-01-22',
    dayName: 'Lun',
    dayNumber: '22',
    month: 'Gen',
    time: '11:00 - 14:00',
    checkOut: '10:00',
    checkIn: '15:00',
    type: 'standard',
    confidence: 95,
    reason: 'Finestra ottimale tra check-out e check-in',
    selected: false
  }, {
    id: 'ai-2',
    date: '2024-01-24',
    dayName: 'Mer',
    dayNumber: '24',
    month: 'Gen',
    time: '12:00 - 14:30',
    checkOut: '11:00',
    checkIn: '15:00',
    type: 'express',
    confidence: 88,
    reason: 'Finestra stretta, consigliata pulizia express',
    selected: false
  }, {
    id: 'ai-3',
    date: '2024-01-26',
    dayName: 'Ven',
    dayNumber: '26',
    month: 'Gen',
    time: '10:00 - 14:00',
    checkOut: '09:00',
    checkIn: '15:00',
    type: 'standard',
    confidence: 92,
    reason: 'Ampia finestra disponibile',
    selected: false
  }, {
    id: 'ai-4',
    date: '2024-01-28',
    dayName: 'Dom',
    dayNumber: '28',
    month: 'Gen',
    time: '11:00 - 15:00',
    checkOut: '10:00',
    checkIn: '16:00',
    type: 'standard',
    confidence: 90,
    reason: 'Dopo 3 soggiorni, consigliata pulizia approfondita',
    selected: false
  }];
  // Available dates for the wizard
  const availableDates = [{
    date: '2024-01-22',
    dayName: 'Lun',
    dayNumber: '22',
    month: 'Gen'
  }, {
    date: '2024-01-23',
    dayName: 'Mar',
    dayNumber: '23',
    month: 'Gen'
  }, {
    date: '2024-01-24',
    dayName: 'Mer',
    dayNumber: '24',
    month: 'Gen'
  }, {
    date: '2024-01-25',
    dayName: 'Gio',
    dayNumber: '25',
    month: 'Gen'
  }, {
    date: '2024-01-26',
    dayName: 'Ven',
    dayNumber: '26',
    month: 'Gen'
  }, {
    date: '2024-01-27',
    dayName: 'Sab',
    dayNumber: '27',
    month: 'Gen'
  }, {
    date: '2024-01-28',
    dayName: 'Dom',
    dayNumber: '28',
    month: 'Gen'
  }];
  // Available time slots
  const availableTimeSlots = [{
    id: '1',
    time: '09:00 - 12:00',
    available: true
  }, {
    id: '2',
    time: '10:00 - 13:00',
    available: true
  }, {
    id: '3',
    time: '11:00 - 14:00',
    available: false
  }, {
    id: '4',
    time: '12:00 - 15:00',
    available: true
  }, {
    id: '5',
    time: '14:00 - 17:00',
    available: true
  }, {
    id: '6',
    time: '15:00 - 18:00',
    available: true
  }];
  // Cleaning types
  const cleaningTypes = [{
    id: 'standard',
    name: 'Pulizia Standard',
    credits: 1,
    duration: '3 ore',
    description: 'Pulizia completa di tutti gli ambienti'
  }, {
    id: 'deep',
    name: 'Deep Cleaning',
    credits: 2,
    duration: '5 ore',
    description: 'Pulizia profonda con sanificazione'
  }, {
    id: 'express',
    name: 'Pulizia Express',
    credits: 1,
    duration: '2 ore',
    description: 'Pulizia rapida per turnover veloci'
  }];
  const resetWizard = () => {
    setWizardMode('choice');
    setWizardStep(0);
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedType(null);
    setIsConfirming(false);
    setIsAnalyzing(false);
    setAiSuggestions([]);
    setAnalysisComplete(false);
  };
  const handleClose = () => {
    onClose();
    setTimeout(resetWizard, 300);
  };
  const handleSelectManual = () => {
    setWizardMode('manual');
    setWizardStep(1);
  };
  const handleSelectAi = () => {
    setWizardMode('ai');
    setWizardStep(1);
    setIsAnalyzing(true);
    // Simulate AI analysis
    setTimeout(() => {
      setIsAnalyzing(false);
      setAiSuggestions(mockAiSuggestions);
      setAnalysisComplete(true);
    }, 2500);
  };
  const toggleAiSuggestion = (id: string) => {
    setAiSuggestions(prev => prev.map(s => s.id === id ? {
      ...s,
      selected: !s.selected
    } : s));
  };
  const getSelectedAiSuggestions = () => {
    return aiSuggestions.filter(s => s.selected);
  };
  const getTotalCredits = () => {
    return getSelectedAiSuggestions().reduce((acc, s) => {
      return acc + (s.type === 'standard' ? 1 : 1);
    }, 0);
  };
  const handleConfirm = () => {
    setIsConfirming(true);
    setTimeout(() => {
      if (wizardMode === 'manual') {
        setWizardStep(4);
      } else {
        setWizardStep(3); // AI success step
      }
      setIsConfirming(false);
    }, 1500);
  };
  const handleBack = () => {
    if (wizardMode === 'choice' || wizardStep === 0) {
      return;
    }
    if (wizardStep === 1) {
      setWizardMode('choice');
      setWizardStep(0);
      setIsAnalyzing(false);
      setAnalysisComplete(false);
      setAiSuggestions([]);
    } else {
      setWizardStep(wizardStep - 1);
    }
  };
  const getSelectedDateInfo = () => {
    return availableDates.find(d => d.date === selectedDate);
  };
  const getSelectedTypeInfo = () => {
    return cleaningTypes.find(t => t.id === selectedType);
  };
  const getStepCount = () => {
    if (wizardMode === 'manual') return 3;
    if (wizardMode === 'ai') return 2;
    return 0;
  };
  const getHeaderTitle = () => {
    if (wizardMode === 'choice') return 'Pianifica Pulizia';
    if (wizardMode === 'ai') {
      if (wizardStep === 3) return 'Pulizie Pianificate!';
      return 'Pianificazione AI';
    }
    if (wizardStep === 4) return 'Pulizia Pianificata!';
    return 'Pianifica Pulizia';
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200" onClick={handleClose} data-id="element-285">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-286">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200" data-id="element-287">
          <div className="flex items-center gap-3" data-id="element-288">
            {wizardStep > 0 && wizardStep < (wizardMode === 'manual' ? 4 : 3) && <button onClick={handleBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors -ml-2" data-id="element-289">
                  <ChevronLeft size={20} className="text-gray-500" data-id="element-290" />
                </button>}
            <div data-id="element-291">
              <h2 className="text-lg font-bold text-navy" data-id="element-292">
                {getHeaderTitle()}
              </h2>
              {wizardMode !== 'choice' && wizardStep > 0 && wizardStep < (wizardMode === 'manual' ? 4 : 3) && <p className="text-xs text-gray-500 mt-0.5" data-id="element-293">
                    Passo {wizardStep} di {getStepCount()}
                  </p>}
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-294">
            <X size={20} className="text-gray-500" data-id="element-295" />
          </button>
        </div>

        {/* Progress Bar */}
        {wizardMode !== 'choice' && wizardStep > 0 && wizardStep < (wizardMode === 'manual' ? 4 : 3) && <div className="px-4 lg:px-6 pt-4" data-id="element-296">
              <div className="flex gap-2" data-id="element-297">
                {Array.from({
            length: getStepCount()
          }, (_, i) => i + 1).map(step => <div key={step} className={`h-1 flex-1 rounded-full transition-colors ${step <= wizardStep ? 'bg-teal-deep' : 'bg-gray-200'}`} data-id="element-298" />)}
              </div>
            </div>}

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-299">
          {/* Step 0: Choose Mode */}
          {wizardMode === 'choice' && <div className="space-y-4" data-id="element-300">
              <div data-id="element-301">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-302">
                  Come vuoi pianificare?
                </h3>
                <p className="text-xs text-gray-500" data-id="element-303">
                  Scegli la modalità di pianificazione
                </p>
              </div>

              <div className="space-y-3" data-id="element-304">
                {/* AI Option */}
                <button onClick={handleSelectAi} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-teal-deep hover:bg-teal-50/50 transition-all group" data-id="element-305">
                  <div className="flex items-start gap-4" data-id="element-306">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center flex-shrink-0" data-id="element-307">
                      <Sparkles size={24} className="text-white" data-id="element-308" />
                    </div>
                    <div className="flex-1" data-id="element-309">
                      <div className="flex items-center gap-2" data-id="element-310">
                        <span className="text-sm font-semibold text-navy group-hover:text-teal-deep transition-colors" data-id="element-311">
                          Pianificazione AI
                        </span>
                        <span className="text-[10px] font-medium px-2 py-0.5 bg-teal-100 text-cyan-700 rounded-full" data-id="element-312">
                          Consigliato
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-313">
                        L'AI analizza i tuoi check-in e check-out e ti propone
                        gli slot migliori per le pulizie
                      </p>
                      <div className="flex items-center gap-3 mt-2" data-id="element-314">
                        <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-315">
                          <CheckCircle size={10} className="text-teal-deep" data-id="element-316" />
                          Analisi automatica
                        </span>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-317">
                          <CheckCircle size={10} className="text-teal-deep" data-id="element-318" />
                          Più slot in una volta
                        </span>
                      </div>
                    </div>
                  </div>
                </button>

                {/* Manual Option */}
                <button onClick={handleSelectManual} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all group" data-id="element-319">
                  <div className="flex items-start gap-4" data-id="element-320">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-321">
                      <MousePointer size={24} className="text-gray-500" data-id="element-322" />
                    </div>
                    <div className="flex-1" data-id="element-323">
                      <span className="text-sm font-semibold text-navy" data-id="element-324">
                        Pianificazione Manuale
                      </span>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-325">
                        Scegli tu giorno, orario e tipo di pulizia passo per
                        passo
                      </p>
                      <div className="flex items-center gap-3 mt-2" data-id="element-326">
                        <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-327">
                          <Calendar size={10} data-id="element-328" />
                          Controllo totale
                        </span>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-329">
                          <Clock size={10} data-id="element-330" />
                          Una pulizia alla volta
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Property Info */}
              <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3 mt-4" data-id="element-331">
                <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center" data-id="element-332">
                  <Home size={18} className="text-gray-400" data-id="element-333" />
                </div>
                <div data-id="element-334">
                  <div className="text-sm font-medium text-navy" data-id="element-335">
                    Villa Sunset
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-336">
                    Proprietà selezionata
                  </div>
                </div>
              </div>
            </div>}

          {/* AI Mode - Step 1: Analysis & Suggestions */}
          {wizardMode === 'ai' && wizardStep === 1 && <div className="space-y-4" data-id="element-337">
              {isAnalyzing ? <div className="text-center py-8" data-id="element-338">
                  <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4" data-id="element-339">
                    <Sparkles size={28} className="text-teal-deep animate-pulse" data-id="element-340" />
                  </div>
                  <h3 className="text-sm font-semibold text-navy mb-2" data-id="element-341">
                    Analisi in corso...
                  </h3>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-342">
                    Sto analizzando i check-in e check-out per trovare gli slot
                    migliori
                  </p>
                  <div className="w-48 h-1 bg-gray-200 rounded-full mx-auto overflow-hidden" data-id="element-343">
                    <div className="h-full bg-teal-deep rounded-full animate-[loading_1.5s_ease-in-out_infinite]" style={{
                width: '60%'
              }} data-id="element-344" />
                  </div>
                </div> : <>
                  <div data-id="element-345">
                    <div className="flex items-center gap-2 mb-1" data-id="element-346">
                      <Sparkles size={16} className="text-coral" data-id="element-347" />
                      <h3 className="text-sm font-semibold text-navy" data-id="element-348">
                        Slot suggeriti dall'AI
                      </h3>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-349">
                      Seleziona gli slot che vuoi confermare
                    </p>
                  </div>

                  <div className="space-y-2" data-id="element-350">
                    {aiSuggestions.map(suggestion => <button key={suggestion.id} onClick={() => toggleAiSuggestion(suggestion.id)} className={`w-full text-left p-4 rounded-xl border-2 transition-all ${suggestion.selected ? 'border-teal-deep bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-351">
                        <div className="flex items-start gap-3" data-id="element-352">
                          {/* Checkbox */}
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${suggestion.selected ? 'bg-teal-deep border-teal-deep' : 'border-gray-300'}`} data-id="element-353">
                            {suggestion.selected && <Check size={12} className="text-white" data-id="element-354" />}
                          </div>

                          {/* Date badge */}
                          <div className="w-12 h-12 rounded-lg flex flex-col items-center justify-center bg-white border border-gray-200 flex-shrink-0" data-id="element-355">
                            <span className="text-[10px] font-medium text-gray-500 uppercase" data-id="element-356">
                              {suggestion.dayName}
                            </span>
                            <span className="text-lg font-bold leading-tight text-navy" data-id="element-357">
                              {suggestion.dayNumber}
                            </span>
                          </div>

                          {/* Details */}
                          <div className="flex-1 min-w-0" data-id="element-358">
                            <div className="flex items-center gap-2" data-id="element-359">
                              <span className="text-sm font-semibold text-navy" data-id="element-360">
                                {suggestion.time}
                              </span>
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${suggestion.type === 'standard' ? 'bg-teal-100 text-cyan-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-361">
                                {suggestion.type === 'standard' ? 'Standard' : 'Express'}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-362">
                              Check-out {suggestion.checkOut} → Check-in{' '}
                              {suggestion.checkIn}
                            </div>
                            <div className="flex items-center gap-1 mt-1" data-id="element-363">
                              <Sparkles size={10} className="text-coral" data-id="element-364" />
                              <span className="text-[10px] text-gray-500" data-id="element-365">
                                {suggestion.reason}
                              </span>
                            </div>
                          </div>

                          {/* Confidence */}
                          <div className="flex items-center gap-1 px-2 py-1 bg-pink-50 rounded-lg flex-shrink-0" data-id="element-366">
                            <span className="text-xs font-bold text-coral" data-id="element-367">
                              {suggestion.confidence}%
                            </span>
                          </div>
                        </div>
                      </button>)}
                  </div>

                  {/* Summary */}
                  {getSelectedAiSuggestions().length > 0 && <div className="bg-gray-50 rounded-xl p-4 space-y-2" data-id="element-368">
                      <div className="flex justify-between text-sm" data-id="element-369">
                        <span className="text-gray-600" data-id="element-370">
                          Pulizie selezionate
                        </span>
                        <span className="font-medium text-navy" data-id="element-371">
                          {getSelectedAiSuggestions().length}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm" data-id="element-372">
                        <span className="text-gray-600" data-id="element-373">Crediti totali</span>
                        <span className="font-bold text-coral" data-id="element-374">
                          {getTotalCredits()} crediti
                        </span>
                      </div>
                    </div>}

                  {/* Credits Warning */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-id="element-375">
                    <CreditCard size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-376" />
                    <div data-id="element-377">
                      <div className="text-xs font-medium text-amber-800" data-id="element-378">
                        Crediti disponibili: 13
                      </div>
                      <div className="text-xs text-amber-700 mt-0.5" data-id="element-379">
                        Dopo questa prenotazione avrai {13 - getTotalCredits()}{' '}
                        crediti
                      </div>
                    </div>
                  </div>
                </>}
            </div>}

          {/* AI Mode - Step 2: Confirmation */}
          {wizardMode === 'ai' && wizardStep === 2 && <div className="space-y-4" data-id="element-380">
              <div data-id="element-381">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-382">
                  Conferma prenotazioni
                </h3>
                <p className="text-xs text-gray-500" data-id="element-383">
                  Verifica i dettagli prima di confermare
                </p>
              </div>

              <div className="space-y-2" data-id="element-384">
                {getSelectedAiSuggestions().map(suggestion => <div key={suggestion.id} className="p-3 bg-gray-50 rounded-lg flex items-center gap-3" data-id="element-385">
                    <div className="w-10 h-10 rounded-lg flex flex-col items-center justify-center bg-white border border-gray-200" data-id="element-386">
                      <span className="text-[9px] font-medium text-gray-500 uppercase" data-id="element-387">
                        {suggestion.dayName}
                      </span>
                      <span className="text-sm font-bold leading-tight text-navy" data-id="element-388">
                        {suggestion.dayNumber}
                      </span>
                    </div>
                    <div className="flex-1" data-id="element-389">
                      <div className="text-sm font-medium text-navy" data-id="element-390">
                        {suggestion.time}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-391">
                        {suggestion.type === 'standard' ? 'Pulizia Standard' : 'Pulizia Express'}{' '}
                        · 1 credito
                      </div>
                    </div>
                    <CheckCircle size={18} className="text-teal-deep" data-id="element-392" />
                  </div>)}
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-2" data-id="element-393">
                <div className="flex justify-between text-sm" data-id="element-394">
                  <span className="text-gray-600" data-id="element-395">Proprietà</span>
                  <span className="font-medium text-navy" data-id="element-396">Villa Sunset</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-397">
                  <span className="text-gray-600" data-id="element-398">Pulizie totali</span>
                  <span className="font-medium text-navy" data-id="element-399">
                    {getSelectedAiSuggestions().length}
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200 flex justify-between text-sm" data-id="element-400">
                  <span className="text-gray-600" data-id="element-401">Crediti da scalare</span>
                  <span className="font-bold text-coral" data-id="element-402">
                    {getTotalCredits()} crediti
                  </span>
                </div>
              </div>
            </div>}

          {/* AI Mode - Step 3: Success */}
          {wizardMode === 'ai' && wizardStep === 3 && <div className="text-center py-6" data-id="element-403">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-404">
                <CheckCircle size={32} className="text-green-600" data-id="element-405" />
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-406">
                {getSelectedAiSuggestions().length} Pulizie Pianificate!
              </h3>
              <p className="text-sm text-gray-600 mb-6" data-id="element-407">
                Le tue pulizie sono state programmate con successo.
              </p>

              <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 mb-6" data-id="element-408">
                {getSelectedAiSuggestions().map(s => <div key={s.id} className="flex justify-between text-sm py-1" data-id="element-409">
                    <span className="text-gray-600" data-id="element-410">
                      {s.dayName} {s.dayNumber} {s.month}
                    </span>
                    <span className="font-medium text-navy" data-id="element-411">{s.time}</span>
                  </div>)}
                <div className="pt-2 border-t border-gray-200 flex justify-between text-sm" data-id="element-412">
                  <span className="text-gray-600" data-id="element-413">Crediti scalati</span>
                  <span className="font-bold text-coral" data-id="element-414">
                    -{getTotalCredits()} crediti
                  </span>
                </div>
              </div>

              <div className="text-xs text-gray-500" data-id="element-415">
                Riceverai una notifica di conferma via email.
              </div>
            </div>}

          {/* Manual Mode - Step 1: Select Date */}
          {wizardMode === 'manual' && wizardStep === 1 && <div className="space-y-4" data-id="element-416">
              <div data-id="element-417">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-418">
                  Seleziona il giorno
                </h3>
                <p className="text-xs text-gray-500" data-id="element-419">
                  Scegli quando vuoi programmare la pulizia
                </p>
              </div>

              <div className="grid grid-cols-7 gap-2" data-id="element-420">
                {availableDates.map(date => <button key={date.date} onClick={() => setSelectedDate(date.date)} className={`flex flex-col items-center justify-center aspect-square rounded-xl border-2 transition-all ${selectedDate === date.date ? 'border-teal-deep bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-421">
                    <span className="text-[10px] font-medium text-gray-500 uppercase" data-id="element-422">
                      {date.dayName}
                    </span>
                    <span className={`text-lg font-bold leading-tight ${selectedDate === date.date ? 'text-teal-deep' : 'text-navy'}`} data-id="element-423">
                      {date.dayNumber}
                    </span>
                    <span className="text-[10px] text-gray-400" data-id="element-424">
                      {date.month}
                    </span>
                  </button>)}
              </div>

              <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3" data-id="element-425">
                <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center" data-id="element-426">
                  <Home size={18} className="text-gray-400" data-id="element-427" />
                </div>
                <div data-id="element-428">
                  <div className="text-sm font-medium text-navy" data-id="element-429">
                    Villa Sunset
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-430">
                    Proprietà selezionata
                  </div>
                </div>
              </div>
            </div>}

          {/* Manual Mode - Step 2: Select Time */}
          {wizardMode === 'manual' && wizardStep === 2 && <div className="space-y-4" data-id="element-431">
              <div data-id="element-432">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-433">
                  Seleziona l'orario
                </h3>
                <p className="text-xs text-gray-500" data-id="element-434">
                  Scegli la fascia oraria per la pulizia
                </p>
              </div>

              <div className="space-y-2" data-id="element-435">
                {availableTimeSlots.map(slot => <button key={slot.id} onClick={() => slot.available && setSelectedTime(slot.time)} disabled={!slot.available} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${!slot.available ? 'border-gray-100 bg-gray-50 cursor-not-allowed' : selectedTime === slot.time ? 'border-teal-deep bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-436">
                    <div className="flex items-center gap-3" data-id="element-437">
                      <Clock size={18} className={!slot.available ? 'text-gray-300' : selectedTime === slot.time ? 'text-teal-deep' : 'text-gray-400'} data-id="element-438" />
                      <span className={`text-sm font-medium ${!slot.available ? 'text-gray-400' : selectedTime === slot.time ? 'text-teal-deep' : 'text-navy'}`} data-id="element-439">
                        {slot.time}
                      </span>
                    </div>
                    {!slot.available && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded" data-id="element-440">
                        Non disponibile
                      </span>}
                    {slot.available && selectedTime === slot.time && <CheckCircle size={18} className="text-teal-deep" data-id="element-441" />}
                  </button>)}
              </div>
            </div>}

          {/* Manual Mode - Step 3: Select Type & Confirm */}
          {wizardMode === 'manual' && wizardStep === 3 && <div className="space-y-4" data-id="element-442">
              <div data-id="element-443">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-444">
                  Tipo di pulizia
                </h3>
                <p className="text-xs text-gray-500" data-id="element-445">
                  Seleziona il tipo di intervento
                </p>
              </div>

              <div className="space-y-2" data-id="element-446">
                {cleaningTypes.map(type => <button key={type.id} onClick={() => setSelectedType(type.id)} className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selectedType === type.id ? 'border-teal-deep bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-447">
                    <div className="flex items-start justify-between" data-id="element-448">
                      <div data-id="element-449">
                        <div className={`text-sm font-semibold ${selectedType === type.id ? 'text-teal-deep' : 'text-navy'}`} data-id="element-450">
                          {type.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5" data-id="element-451">
                          {type.description}
                        </div>
                        <div className="text-xs text-gray-400 mt-1" data-id="element-452">
                          Durata: {type.duration}
                        </div>
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${selectedType === type.id ? 'bg-teal-100' : 'bg-gray-100'}`} data-id="element-453">
                        <CreditCard size={12} className={selectedType === type.id ? 'text-teal-deep' : 'text-gray-500'} data-id="element-454" />
                        <span className={`text-xs font-bold ${selectedType === type.id ? 'text-teal-deep' : 'text-gray-600'}`} data-id="element-455">
                          {type.credits} credito{type.credits > 1 ? 'i' : ''}
                        </span>
                      </div>
                    </div>
                  </button>)}
              </div>

              {selectedType && <div className="bg-gray-50 rounded-xl p-4 space-y-3" data-id="element-456">
                  <div className="text-xs text-gray-400 uppercase tracking-wider font-medium" data-id="element-457">
                    Riepilogo
                  </div>
                  <div className="space-y-2" data-id="element-458">
                    <div className="flex justify-between text-sm" data-id="element-459">
                      <span className="text-gray-600" data-id="element-460">Proprietà</span>
                      <span className="font-medium text-navy" data-id="element-461">
                        Villa Sunset
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-462">
                      <span className="text-gray-600" data-id="element-463">Data</span>
                      <span className="font-medium text-navy" data-id="element-464">
                        {getSelectedDateInfo()?.dayName}{' '}
                        {getSelectedDateInfo()?.dayNumber}{' '}
                        {getSelectedDateInfo()?.month}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-465">
                      <span className="text-gray-600" data-id="element-466">Orario</span>
                      <span className="font-medium text-navy" data-id="element-467">
                        {selectedTime}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-468">
                      <span className="text-gray-600" data-id="element-469">Tipo</span>
                      <span className="font-medium text-navy" data-id="element-470">
                        {getSelectedTypeInfo()?.name}
                      </span>
                    </div>
                    <div className="pt-2 border-t border-gray-200 flex justify-between text-sm" data-id="element-471">
                      <span className="text-gray-600" data-id="element-472">Crediti da scalare</span>
                      <span className="font-bold text-coral" data-id="element-473">
                        {getSelectedTypeInfo()?.credits} credito
                        {(getSelectedTypeInfo()?.credits || 0) > 1 ? 'i' : ''}
                      </span>
                    </div>
                  </div>
                </div>}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-id="element-474">
                <CreditCard size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-475" />
                <div data-id="element-476">
                  <div className="text-xs font-medium text-amber-800" data-id="element-477">
                    Crediti disponibili: 13
                  </div>
                  <div className="text-xs text-amber-700 mt-0.5" data-id="element-478">
                    Dopo questa prenotazione avrai{' '}
                    {13 - (getSelectedTypeInfo()?.credits || 0)} crediti
                  </div>
                </div>
              </div>
            </div>}

          {/* Manual Mode - Step 4: Success */}
          {wizardMode === 'manual' && wizardStep === 4 && <div className="text-center py-6" data-id="element-479">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-480">
                <CheckCircle size={32} className="text-green-600" data-id="element-481" />
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-482">
                Pulizia Pianificata!
              </h3>
              <p className="text-sm text-gray-600 mb-6" data-id="element-483">
                La tua pulizia è stata programmata con successo.
              </p>

              <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 mb-6" data-id="element-484">
                <div className="flex justify-between text-sm" data-id="element-485">
                  <span className="text-gray-600" data-id="element-486">Proprietà</span>
                  <span className="font-medium text-navy" data-id="element-487">Villa Sunset</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-488">
                  <span className="text-gray-600" data-id="element-489">Data</span>
                  <span className="font-medium text-navy" data-id="element-490">
                    {getSelectedDateInfo()?.dayName}{' '}
                    {getSelectedDateInfo()?.dayNumber}{' '}
                    {getSelectedDateInfo()?.month}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-491">
                  <span className="text-gray-600" data-id="element-492">Orario</span>
                  <span className="font-medium text-navy" data-id="element-493">{selectedTime}</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-494">
                  <span className="text-gray-600" data-id="element-495">Tipo</span>
                  <span className="font-medium text-navy" data-id="element-496">
                    {getSelectedTypeInfo()?.name}
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200 flex justify-between text-sm" data-id="element-497">
                  <span className="text-gray-600" data-id="element-498">Crediti scalati</span>
                  <span className="font-bold text-coral" data-id="element-499">
                    -{getSelectedTypeInfo()?.credits} credito
                    {(getSelectedTypeInfo()?.credits || 0) > 1 ? 'i' : ''}
                  </span>
                </div>
              </div>

              <div className="text-xs text-gray-500" data-id="element-500">
                Riceverai una notifica di conferma via email.
              </div>
            </div>}
        </div>

        {/* Modal Footer */}
        <div className="p-4 lg:p-6 border-t border-gray-200" data-id="element-501">
          {/* Choice mode - no footer */}
          {wizardMode === 'choice' && <div className="text-xs text-center text-gray-400" data-id="element-502">
              Seleziona una modalità per continuare
            </div>}

          {/* AI Mode - Step 1 */}
          {wizardMode === 'ai' && wizardStep === 1 && !isAnalyzing && <button onClick={() => setWizardStep(2)} disabled={getSelectedAiSuggestions().length === 0} className={`w-full px-4 py-3 rounded-full text-sm font-medium transition-colors min-h-[48px] ${getSelectedAiSuggestions().length > 0 ? 'bg-teal-deep text-white hover:bg-[#50BFD9]' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-503">
              Continua con {getSelectedAiSuggestions().length} pulizie
            </button>}

          {/* AI Mode - Step 2 */}
          {wizardMode === 'ai' && wizardStep === 2 && <button onClick={handleConfirm} disabled={isConfirming} className={`w-full px-4 py-3 rounded-full text-sm font-medium transition-colors min-h-[48px] flex items-center justify-center gap-2 ${!isConfirming ? 'bg-teal-deep text-white hover:bg-[#50BFD9]' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-504">
              {isConfirming ? <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-505" />
                  Conferma in corso...
                </> : <>
                  <CheckCircle size={18} data-id="element-506" />
                  Conferma e Scala {getTotalCredits()} Crediti
                </>}
            </button>}

          {/* AI Mode - Step 3 (Success) */}
          {wizardMode === 'ai' && wizardStep === 3 && <div className="flex gap-3" data-id="element-507">
              <Link to="/pulizie" onClick={handleClose} className="flex-1 px-4 py-3 bg-gray-100 text-navy rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors min-h-[48px] flex items-center justify-center" data-id="element-508">
                Vai alle Pulizie
              </Link>
              <button onClick={handleClose} className="w-full px-4 py-3 bg-teal-deep text-white rounded-full text-sm font-medium hover:bg-[#50BFD9] transition-colors min-h-[48px]" data-id="element-509">
                Chiudi
              </button>
            </div>}

          {/* Manual Mode - Step 1 */}
          {wizardMode === 'manual' && wizardStep === 1 && <button onClick={() => setWizardStep(2)} disabled={!selectedDate} className={`w-full px-4 py-3 rounded-full text-sm font-medium transition-colors min-h-[48px] ${selectedDate ? 'bg-teal-deep text-white hover:bg-[#50BFD9]' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-510">
              Continua
            </button>}

          {/* Manual Mode - Step 2 */}
          {wizardMode === 'manual' && wizardStep === 2 && <button onClick={() => setWizardStep(3)} disabled={!selectedTime} className={`w-full px-4 py-3 rounded-full text-sm font-medium transition-colors min-h-[48px] ${selectedTime ? 'bg-teal-deep text-white hover:bg-[#50BFD9]' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-511">
              Continua
            </button>}

          {/* Manual Mode - Step 3 */}
          {wizardMode === 'manual' && wizardStep === 3 && <button onClick={handleConfirm} disabled={!selectedType || isConfirming} className={`w-full px-4 py-3 rounded-full text-sm font-medium transition-colors min-h-[48px] flex items-center justify-center gap-2 ${selectedType && !isConfirming ? 'bg-teal-deep text-white hover:bg-[#50BFD9]' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-512">
              {isConfirming ? <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-513" />
                  Conferma in corso...
                </> : <>
                  <CheckCircle size={18} data-id="element-514" />
                  Conferma e Scala Crediti
                </>}
            </button>}

          {/* Manual Mode - Step 4 (Success) */}
          {wizardMode === 'manual' && wizardStep === 4 && <div className="flex gap-3" data-id="element-515">
              <Link to="/pulizie" onClick={handleClose} className="flex-1 px-4 py-3 bg-gray-100 text-navy rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors min-h-[48px] flex items-center justify-center" data-id="element-516">
                Vai alle Pulizie
              </Link>
              <button onClick={handleClose} className="w-full px-4 py-3 bg-teal-deep text-white rounded-full text-sm font-medium hover:bg-[#50BFD9] transition-colors min-h-[48px]" data-id="element-517">
                Chiudi
              </button>
            </div>}
        </div>
      </div>
    </div>;
}